import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import {
  FileText, MagnifyingGlass, ShieldCheck, XCircle, ArrowClockwise,
  Warning, Eye, Phone, Envelope, ClockCounterClockwise,
} from "@phosphor-icons/react";

const TABS = [
  { key: "pending",  label: "Pending",  colour: "text-amber-700" },
  { key: "approved", label: "Approved", colour: "text-emerald-700" },
  { key: "rejected", label: "Rejected", colour: "text-red-700" },
  { key: "all",      label: "All",      colour: "text-slate-700" },
];

export default function AdminDocuments() {
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], counts: {} });
  const [busy, setBusy] = useState(false);
  const [reject, setReject] = useState({ open: false, doc: null, reason: "" });
  const [bulkReject, setBulkReject] = useState({ open: false, reason: "" });
  const [preview, setPreview] = useState({ open: false, doc: null });
  const [selected, setSelected] = useState(() => new Set());

  const load = async () => {
    try {
      const r = await api.get("/admin/documents", { params: { status: tab, q: q || undefined } });
      setData(r.data);
      setSelected(new Set()); // reset selection whenever list refreshes
    } catch { toast.error("Could not load documents"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const items = data.items || [];
  const counts = data.counts || {};
  const pendingItems = useMemo(() => items.filter((d) => d.verification_status === "pending"), [items]);
  const showBulkBar = tab === "pending" && pendingItems.length > 0;
  const allSelected = showBulkBar && pendingItems.length > 0 && pendingItems.every((d) => selected.has(d.id));

  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(pendingItems.map((d) => d.id)));
  };

  const approve = async (d) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.patch(`/admin/documents/${d.id}?status=approved`);
      toast.success(`Approved · ${d.document_type}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve");
    } finally { setBusy(false); }
  };

  const bulkApprove = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} document${ids.length === 1 ? "" : "s"} in one go?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/documents/bulk-approve", { document_ids: ids });
      const { approved, skipped } = r.data || {};
      toast.success(`Approved ${approved}${skipped ? ` · skipped ${skipped}` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approve failed");
    } finally { setBusy(false); }
  };

  const doBulkReject = async () => {
    const ids = Array.from(selected);
    const reason = bulkReject.reason.trim();
    if (ids.length === 0) return;
    if (reason.length < 4) { toast.error("Please write a short shared reason (min 4 chars)"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/documents/bulk-reject", { document_ids: ids, reason });
      const { rejected, skipped } = r.data || {};
      toast.success(`Rejected ${rejected}${skipped ? ` · skipped ${skipped}` : ""} — staff notified`);
      setBulkReject({ open: false, reason: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk reject failed");
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    const reason = reject.reason.trim();
    if (reason.length < 4) { toast.error("Please write a short reason (min 4 chars)"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/documents/${reject.doc.id}?status=rejected&reason=${encodeURIComponent(reason)}`);
      toast.success("Rejected — staff will get a notification & email");
      setReject({ open: false, doc: null, reason: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject");
    } finally { setBusy(false); }
  };

  const reopen = async (d) => {
    setBusy(true);
    try {
      await api.patch(`/admin/documents/${d.id}?status=pending`);
      toast.success("Moved back to pending");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-documents-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="label-caps">Compliance</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">
            Document review
          </h1>
          <p className="text-slate-600 mt-1">
            Approve or reject staff-uploaded ID, right-to-work and certifications. Every decision is logged.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="admin-docs-refresh">
            <ArrowClockwise size={14} className="mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex items-center gap-2 border-b border-slate-200 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`admin-docs-tab-${t.key}`}
            className={`relative px-4 py-2 text-sm font-semibold whitespace-nowrap transition ${
              tab === t.key
                ? "text-slate-900 border-b-2 border-blue-600 -mb-px"
                : "text-slate-500 hover:text-slate-800 border-b-2 border-transparent -mb-px"
            }`}
          >
            <span className={tab === t.key ? "" : t.colour}>{t.label}</span>
            {counts[t.key] != null && (
              <span
                className={`ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-5 px-1.5 text-[11px] font-bold rounded-full ${
                  tab === t.key ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"
                }`}
                data-testid={`admin-docs-count-${t.key}`}
              >
                {counts[t.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search staff name, email, or file name…"
          className="pl-9 h-10"
          data-testid="admin-docs-search"
        />
      </div>

      {/* Bulk-select bar */}
      {showBulkBar && (
        <div
          data-testid="admin-docs-bulk-bar"
          className={`sticky top-2 z-10 flex items-center justify-between gap-3 rounded-md border px-4 py-3 shadow-sm transition ${
            selected.size > 0 ? "bg-blue-50 border-blue-200" : "bg-white border-slate-200"
          }`}
        >
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleAll}
              data-testid="admin-docs-select-all"
            />
            {allSelected ? "Deselect all" : `Select all ${pendingItems.length} pending`}
          </label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600" data-testid="admin-docs-selected-count">
              {selected.size} selected
            </span>
            <Button
              size="sm"
              disabled={busy || selected.size === 0}
              onClick={() => setBulkReject({ open: true, reason: "" })}
              variant="outline"
              className="h-9 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="admin-docs-bulk-reject-btn"
            >
              <XCircle size={14} className="mr-1.5" /> Reject {selected.size || ""}
            </Button>
            <Button
              size="sm"
              disabled={busy || selected.size === 0}
              onClick={bulkApprove}
              className="bg-emerald-600 hover:bg-emerald-700 h-9"
              data-testid="admin-docs-bulk-approve-btn"
            >
              <ShieldCheck size={14} className="mr-1.5" /> Approve {selected.size || ""}
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      {items.length === 0 && (
        <div className="card-tactile p-10 text-center text-slate-500" data-testid="admin-docs-empty">
          {tab === "pending" ? "🎉 No documents waiting for review." : "No documents in this view."}
        </div>
      )}

      <div className="space-y-3">
        {items.map((d) => (
          <DocRow
            key={d.id}
            d={d}
            busy={busy}
            selectable={d.verification_status === "pending"}
            selected={selected.has(d.id)}
            onToggle={() => toggleOne(d.id)}
            onPreview={() => setPreview({ open: true, doc: d })}
            onApprove={() => approve(d)}
            onReject={() => setReject({ open: true, doc: d, reason: "" })}
            onReopen={() => reopen(d)}
          />
        ))}
      </div>

      {/* Preview dialog */}
      <Dialog open={preview.open} onOpenChange={(v) => setPreview((p) => ({ ...p, open: v }))}>
        <DialogContent className="max-w-4xl" data-testid="admin-doc-preview-dialog">
          {preview.doc && <PreviewBody d={preview.doc} />}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={reject.open} onOpenChange={(v) => setReject((r) => ({ ...r, open: v, reason: v ? r.reason : "" }))}>
        <DialogContent data-testid="admin-doc-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject this document?</DialogTitle>
            <DialogDescription>
              The staff member will receive a notification and email with your reason so they can fix and re-upload.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="label-caps">Reason (staff will read this)</Label>
            <Textarea
              rows={4}
              value={reject.reason}
              onChange={(e) => setReject((r) => ({ ...r, reason: e.target.value }))}
              placeholder="e.g. Photo is blurred. Please re-upload with better lighting and no glare."
              data-testid="admin-doc-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject({ open: false, doc: null, reason: "" })} data-testid="admin-doc-reject-cancel">
              Cancel
            </Button>
            <Button
              onClick={doReject}
              disabled={busy || reject.reason.trim().length < 4}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-doc-reject-confirm"
            >
              Reject document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk reject dialog */}
      <Dialog open={bulkReject.open} onOpenChange={(v) => setBulkReject((r) => ({ ...r, open: v, reason: v ? r.reason : "" }))}>
        <DialogContent data-testid="admin-doc-bulk-reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reject {selected.size} document{selected.size === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>
              Everyone selected will receive the <b>same reason</b> in their notification and email so they know exactly what to fix.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="label-caps">Shared reason</Label>
            <Textarea
              rows={4}
              value={bulkReject.reason}
              onChange={(e) => setBulkReject((r) => ({ ...r, reason: e.target.value }))}
              placeholder="e.g. Document is out of date — please re-upload the current version."
              data-testid="admin-doc-bulk-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkReject({ open: false, reason: "" })} data-testid="admin-doc-bulk-reject-cancel">
              Cancel
            </Button>
            <Button
              onClick={doBulkReject}
              disabled={busy || bulkReject.reason.trim().length < 4 || selected.size === 0}
              className="bg-red-600 hover:bg-red-700"
              data-testid="admin-doc-bulk-reject-confirm"
            >
              Reject {selected.size} document{selected.size === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Row ----------
function DocRow({ d, busy, selectable, selected, onToggle, onPreview, onApprove, onReject, onReopen }) {
  const isImage = (d.content_type || "").startsWith("image/");
  const status = d.verification_status;
  const name = `${d.staff?.first_name || ""} ${d.staff?.last_name || ""}`.trim() || d.staff?.email || "Unknown";
  return (
    <div
      className={`card-tactile p-4 sm:p-5 grid grid-cols-1 sm:grid-cols-[auto_100px_1fr_auto] gap-4 ${selected ? "ring-2 ring-blue-300 bg-blue-50/30" : ""}`}
      data-testid={`doc-row-${d.id}`}
    >
      {/* Select */}
      <div className="pt-1 sm:pt-2 flex sm:block items-center gap-2">
        {selectable ? (
          <Checkbox
            checked={selected}
            onCheckedChange={onToggle}
            data-testid={`doc-select-${d.id}`}
          />
        ) : (
          <div className="w-4 h-4 rounded-sm bg-slate-100" />
        )}
      </div>

      {/* Thumbnail */}
      <button
        type="button"
        onClick={onPreview}
        className="w-full sm:w-24 h-24 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden hover:border-blue-400 transition"
        data-testid={`doc-thumb-${d.id}`}
        title="Preview"
      >
        {isImage ? (
          <img src={fileUrl(d.file_path)} alt="doc" className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <FileText size={40} className="text-slate-400" />
        )}
      </button>

      {/* Meta */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display text-lg font-bold text-slate-900 truncate">{d.document_type}</div>
          {d.is_expired && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-red-200 bg-red-50 text-red-700 text-xs font-semibold" data-testid={`doc-expired-${d.id}`}>
              <Warning size={12} weight="fill" /> Expired {d.expiry_date}
            </span>
          )}
        </div>
        <div className="text-sm text-slate-600 truncate">{d.original_filename}</div>
        <div className="mt-2 flex items-center gap-3 flex-wrap text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{name}</span>
          {d.staff?.email && (
            <span className="flex items-center gap-1 text-slate-500"><Envelope size={12}/> {d.staff.email}</span>
          )}
          {d.staff?.phone && (
            <span className="flex items-center gap-1 text-slate-500"><Phone size={12}/> {d.staff.phone}</span>
          )}
          {d.staff?.status && (
            <span className="text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold">
              {d.staff.status.replace("_"," ")}
            </span>
          )}
        </div>
        {d.review_reason && status === "rejected" && (
          <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-2 py-1.5">
            <b>Rejection reason:</b> {d.review_reason}
          </div>
        )}
        {d.reviewed_by_email && status !== "pending" && (
          <div className="mt-1.5 text-[11px] text-slate-500 flex items-center gap-1">
            <ClockCounterClockwise size={12}/> {status} by {d.reviewed_by_email} · {(d.reviewed_at || "").slice(0,16).replace("T"," ")}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex sm:flex-col items-start sm:items-end gap-2">
        <StatusBadge status={status} />
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={onPreview} className="h-9" data-testid={`doc-preview-${d.id}`}>
            <Eye size={14} className="mr-1.5" /> Preview
          </Button>
          {status !== "approved" && (
            <Button size="sm" disabled={busy} onClick={onApprove} className="bg-emerald-600 hover:bg-emerald-700 h-9" data-testid={`doc-approve-${d.id}`}>
              <ShieldCheck size={14} className="mr-1.5" /> Approve
            </Button>
          )}
          {status !== "rejected" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onReject} className="h-9 text-red-600 border-red-200 hover:bg-red-50" data-testid={`doc-reject-${d.id}`}>
              <XCircle size={14} className="mr-1.5" /> Reject
            </Button>
          )}
          {status !== "pending" && (
            <Button size="sm" variant="outline" disabled={busy} onClick={onReopen} className="h-9 text-slate-700" data-testid={`doc-reopen-${d.id}`}>
              Reopen
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Preview ----------
function PreviewBody({ d }) {
  const isImage = (d.content_type || "").startsWith("image/");
  const isPdf = (d.content_type || "").includes("pdf") || (d.original_filename || "").toLowerCase().endsWith(".pdf");
  const src = fileUrl(d.file_path);
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display">{d.document_type}</DialogTitle>
        <DialogDescription>
          {d.original_filename}
          {d.expiry_date && (
            <span className={`ml-2 font-semibold ${d.is_expired ? "text-red-600" : "text-slate-600"}`}>
              · expires {d.expiry_date}
            </span>
          )}
        </DialogDescription>
      </DialogHeader>
      <div className="rounded-md border border-slate-200 bg-slate-50 overflow-hidden max-h-[70vh]">
        {isImage && (
          <img src={src} alt={d.original_filename} className="w-full max-h-[70vh] object-contain bg-slate-100" data-testid={`doc-preview-img-${d.id}`} />
        )}
        {!isImage && isPdf && (
          <iframe
            src={src}
            title="PDF preview"
            className="w-full h-[70vh]"
            sandbox=""
            referrerPolicy="no-referrer"
            data-testid={`doc-preview-pdf-${d.id}`}
          />
        )}
        {!isImage && !isPdf && (
          <div className="p-8 text-center text-slate-500">
            Preview not available for this file type.<br />
            <a href={src} target="_blank" rel="noreferrer" className="text-blue-600 font-semibold underline">
              Open in new tab
            </a>
          </div>
        )}
      </div>
    </>
  );
}
